/**
 * Service pour la gestion des permissions côté frontend
 */

import api from './api';
import {
  Permission,
  Role,
  RolePermission,
  UserRole,
  PermissionFormData,
  RoleFormData,
  AssignPermissionToRoleData,
  RemovePermissionFromRoleData,
  PermissionCategory,
  PermissionManagementStats,
  PaginatedResponse,
  PaginationParams,
  PermissionFilters,
  RoleFilters
} from '@/types/permissionManagement';

class PermissionManagementService {
  private baseUrl = '/permissions';

  private normalizePaginatedResponse<T>(data: unknown): PaginatedResponse<T> {
    const payload = data as Record<string, unknown>;

    if (payload?.results && Array.isArray(payload.results)) {
      return payload as unknown as PaginatedResponse<T>;
    }

    if (payload?.data && typeof payload.data === 'object') {
      const nested = payload.data as Record<string, unknown>;
      if (nested.results && Array.isArray(nested.results)) {
        return nested as unknown as PaginatedResponse<T>;
      }
      if (Array.isArray(nested)) {
        return {
          count: nested.length,
          results: nested as T[],
          next: undefined,
          previous: undefined,
        };
      }
    }

    if (Array.isArray(data)) {
      return {
        count: data.length,
        results: data as T[],
        next: undefined,
        previous: undefined,
      };
    }

    return {
      count: 0,
      results: [],
      next: undefined,
      previous: undefined,
    };
  }

  private normalizePermission(permission: Permission): Permission {
    return {
      ...permission,
      code: permission.code || permission.name,
      access_level: (permission.access_level || (permission as any).default_access_level || 'read') as Permission['access_level'],
    };
  }

  // ============================================================================
  // GESTION DES PERMISSIONS
  // ============================================================================

  /**
   * Récupérer toutes les permissions avec pagination et filtres
   */
  async getPermissions(
    params: PaginationParams & PermissionFilters = { page: 1, page_size: 20 }
  ): Promise<PaginatedResponse<Permission>> {
    const response = await api.get(`${this.baseUrl}/permissions/`, { params });
    const paginated = this.normalizePaginatedResponse<Permission>(response.data);
    return {
      ...paginated,
      results: paginated.results.map((permission) => this.normalizePermission(permission)),
    };
  }

  /**
   * Récupérer une permission par ID
   */
  async getPermission(id: number): Promise<Permission> {
    const response = await api.get(`${this.baseUrl}/permissions/${id}/`);
    return this.normalizePermission(response.data);
  }

  /**
   * Créer une nouvelle permission
   */
  async createPermission(data: PermissionFormData): Promise<Permission> {
    const response = await api.post(`${this.baseUrl}/permissions/`, data);
    return response.data;
  }

  /**
   * Mettre à jour une permission
   */
  async updatePermission(id: number, data: PermissionFormData): Promise<Permission> {
    const response = await api.patch(`${this.baseUrl}/permissions/${id}/`, data);
    return response.data;
  }

  /**
   * Supprimer une permission
   */
  async deletePermission(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/permissions/${id}/`);
  }

  /**
   * Récupérer les permissions par catégorie
   */
  async getPermissionsByCategory(): Promise<PermissionCategory[]> {
    const response = await api.get(`${this.baseUrl}/permissions/by-category/`);
    return response.data;
  }

  // ============================================================================
  // GESTION DES RÔLES
  // ============================================================================

  /**
   * Récupérer tous les rôles avec pagination et filtres
   */
  async getRoles(
    params: PaginationParams & RoleFilters = { page: 1, page_size: 20 }
  ): Promise<PaginatedResponse<Role>> {
    const response = await api.get(`${this.baseUrl}/roles/`, { params });
    return this.normalizePaginatedResponse<Role>(response.data);
  }

  /**
   * Récupérer un rôle par ID
   */
  async getRole(id: number): Promise<Role> {
    const response = await api.get(`${this.baseUrl}/roles/${id}/`);
    return response.data;
  }

  /**
   * Récupérer les utilisateurs assignés à un rôle
   */
  async getRoleUsers(roleId: number): Promise<UserRole[]> {
    const response = await api.get(`${this.baseUrl}/roles/${roleId}/users/`);
    return response.data;
  }

  /**
   * Créer un nouveau rôle
   */
  async createRole(data: RoleFormData): Promise<Role> {
    const response = await api.post(`${this.baseUrl}/roles/`, data);
    return response.data;
  }

  /**
   * Mettre à jour un rôle
   */
  async updateRole(id: number, data: RoleFormData): Promise<Role> {
    const response = await api.patch(`${this.baseUrl}/roles/${id}/`, data);
    return response.data;
  }

  /**
   * Supprimer un rôle
   */
  async deleteRole(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/roles/${id}/`);
  }

  // ============================================================================
  // GESTION DES ASSOCIATIONS RÔLE-PERMISSION
  // ============================================================================

  /**
   * Récupérer les permissions d'un rôle spécifique
   */
  async getRolePermissionsById(roleId: number): Promise<Permission[]> {
    const response = await api.get(`${this.baseUrl}/roles/${roleId}/permissions/`);
    const data = Array.isArray(response.data) ? response.data : response.data?.results || [];
    return data.map((permission: Permission) => this.normalizePermission(permission));
  }

  /**
   * Associer une permission à un rôle
   */
  async assignPermissionToRole(data: AssignPermissionToRoleData): Promise<RolePermission> {
    const response = await api.post(`${this.baseUrl}/role-permissions/`, data);
    return response.data;
  }

  /**
   * Retirer une permission d'un rôle
   */
  async removePermissionFromRole(data: RemovePermissionFromRoleData): Promise<void> {
    await api.delete(`${this.baseUrl}/role-permissions/remove/`, { data });
  }

  /**
   * Récupérer toutes les associations rôle-permission
   */
  async getRolePermissions(): Promise<RolePermission[]> {
    const response = await api.get(`${this.baseUrl}/role-permissions/`);
    return response.data;
  }

  // ============================================================================
  // STATISTIQUES ET DONNÉES GLOBALES
  // ============================================================================

  /**
   * Récupérer les statistiques de gestion des permissions
   */
  async getStats(): Promise<PermissionManagementStats> {
    const response = await api.get(`${this.baseUrl}/permissions/stats/`);
    const payload = (response.data as { data?: PermissionManagementStats })?.data ?? response.data;
    return payload as PermissionManagementStats;
  }

  /**
   * Récupérer toutes les catégories de permissions disponibles
   */
  async getPermissionCategories(): Promise<string[]> {
    const response = await api.get(`${this.baseUrl}/permissions/categories/`);
    return response.data;
  }

  /**
   * Récupérer tous les niveaux d'accès disponibles
   */
  async getAccessLevels(): Promise<string[]> {
    const response = await api.get(`${this.baseUrl}/permissions/access-levels/`);
    return response.data;
  }

  // ============================================================================
  // MÉTHODES UTILITAIRES
  // ============================================================================

  /**
   * Rechercher des permissions par code ou nom
   */
  async searchPermissions(query: string): Promise<Permission[]> {
    const response = await api.get(`${this.baseUrl}/permissions/search/`, {
      params: { q: query }
    });
    return response.data;
  }

  /**
   * Rechercher des rôles par nom
   */
  async searchRoles(query: string): Promise<Role[]> {
    const response = await api.get(`${this.baseUrl}/roles/search/`, {
      params: { q: query }
    });
    return response.data;
  }

  /**
   * Vérifier si une permission existe déjà
   */
  async checkPermissionExists(code: string): Promise<boolean> {
    try {
      await api.get(`${this.baseUrl}/permissions/check-exists/`, {
        params: { code }
      });
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Vérifier si un rôle existe déjà
   */
  async checkRoleExists(name: string): Promise<boolean> {
    try {
      await api.get(`${this.baseUrl}/roles/check-exists/`, {
        params: { name }
      });
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Exporter les permissions en CSV
   */
  async exportPermissions(): Promise<Blob> {
    const response = await api.get(`${this.baseUrl}/permissions/export/`, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Exporter les rôles en CSV
   */
  async exportRoles(): Promise<Blob> {
    const response = await api.get(`${this.baseUrl}/roles/export/`, {
      responseType: 'blob'
    });
    return response.data;
  }
}

// Instance singleton du service
const permissionManagementService = new PermissionManagementService();
export default permissionManagementService;
